/* =============================================================
   HOME PAGE — Full single-page layout
   Design: "Precision & Trust" — Corporate Modernism meets Australian Warmth
   Sections: Hero → TrustBand → Services → WhyUs → Process → Stats → Pricing → Industries → Testimonials → FAQ → Contact → Footer
   ============================================================= */

import Navbar from "@/components/Navbar";
import HeroSection from "@/components/HeroSection";
import TrustBand from "@/components/TrustBand";
import ServicesSection from "@/components/ServicesSection";
import WhyUsSection from "@/components/WhyUsSection";
import ProcessSection from "@/components/ProcessSection";
import StatsBand from "@/components/StatsBand";
import IndustriesSection from "@/components/IndustriesSection";
import BookkeepingServicesLinks from "@/components/BookkeepingServicesLinks";
import SuccessStoriesSection from "@/components/SuccessStoriesSection";
import StickyWhatsApp from "@/components/StickyWhatsApp";
import Footer from "@/components/Footer";
import StickyMobileCTA from "@/components/StickyMobileCTA";
import { useCanonicalTag } from "@/hooks/useCanonicalTag";
import { useEffect, lazy, Suspense } from "react";

// Dynamically import heavy below-the-fold components to reduce initial bundle
// These will be code-split and loaded on-demand when needed
const ROICalculator = lazy(() => import("@/components/ROICalculator"));
const PricingCreative = lazy(() => import("@/components/PricingCreative"));
const PricingFAQ = lazy(() => import("@/components/PricingFAQ"));
const TestimonialsAnimated = lazy(() => import("@/components/TestimonialsAnimated"));
const WhySwitchSection = lazy(() => import("@/components/WhySwitchSection"));
const FAQSection = lazy(() => import("@/components/FAQSection"));
const ContactSection = lazy(() => import("@/components/ContactSection"));

// Minimal loading fallback for below-the-fold sections
const SectionLoader = () => <div style={{ minHeight: "400px" }} />;

export default function Home() {
  useCanonicalTag("/");

  useEffect(() => {
    const scrollToContact = () => {
      // Wait for page to fully render before scrolling
      setTimeout(() => {
        const el = document.getElementById('contact');
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 600);
    };

    // Handle sessionStorage flag (set by other pages before navigating)
    const shouldScrollToContact = sessionStorage.getItem('scrollToContact');
    if (shouldScrollToContact) {
      sessionStorage.removeItem('scrollToContact');
      scrollToContact();
      return;
    }

    // Handle URL hash directly (e.g. /#contact from blog posts)
    if (window.location.hash === '#contact') {
      scrollToContact();
    }
  }, []);

  return (
    <div className="min-h-screen">
      <Navbar />
      <HeroSection />
      <TrustBand />
      <div id="services">
        <ServicesSection />
      </div>
      <WhyUsSection />
      
      {/* Lazy-load ROI Calculator - heavy component with calculations */}
      <Suspense fallback={<SectionLoader />}>
        <ROICalculator />
      </Suspense>
      
      <ProcessSection />
      <StatsBand />
      
      {/* Lazy-load Pricing section - animated components */}
      <div id="pricing">
        <Suspense fallback={<SectionLoader />}>
          <PricingCreative />
          <PricingFAQ />
        </Suspense>
      </div>
      
      <IndustriesSection />
      <BookkeepingServicesLinks />
      
      {/* Lazy-load Testimonials - heavy animation library */}
      <Suspense fallback={<SectionLoader />}>
        <TestimonialsAnimated />
      </Suspense>
      
      {/* Lazy-load WhySwitch - animated section */}
      <Suspense fallback={<SectionLoader />}>
        <WhySwitchSection />
      </Suspense>
      
      <SuccessStoriesSection />
      
      {/* Lazy-load FAQ - accordion with animations */}
      <div id="faq">
        <Suspense fallback={<SectionLoader />}>
          <FAQSection />
        </Suspense>
      </div>
      
      {/* Lazy-load Contact section - form handling */}
      <div id="contact">
        <Suspense fallback={<SectionLoader />}>
          <ContactSection />
        </Suspense>
      </div>
      
      <Footer />
      <StickyMobileCTA />
      <StickyWhatsApp />
      {/* Bottom padding for mobile sticky CTA */}
      <div className="lg:hidden h-14" />

      {/* Consolidated FAQPage structured data — all homepage FAQ questions in one block */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": [
          { "@type": "Question", "name": "Can I change packages or cancel anytime?", "acceptedAnswer": { "@type": "Answer", "text": "Yes, you can upgrade or downgrade your package at any time. Changes take effect from the next billing cycle. We offer month-to-month flexibility with no long-term contracts required." } },
          { "@type": "Question", "name": "What's included in the onboarding process?", "acceptedAnswer": { "@type": "Answer", "text": "Onboarding includes setting up your Xero account, importing historical transactions, configuring chart of accounts, and a comprehensive walkthrough of your bookkeeping setup. We'll ensure you're comfortable with the process and answer all your questions." } },
          { "@type": "Question", "name": "How long does onboarding take?", "acceptedAnswer": { "@type": "Answer", "text": "Onboarding typically takes 1-2 weeks depending on the complexity of your business and data availability. We'll work with you to minimise disruption to your operations." } },
          { "@type": "Question", "name": "Do you offer a trial period or free consultation?", "acceptedAnswer": { "@type": "Answer", "text": "Absolutely! We offer a free 30-minute consultation to discuss your bookkeeping needs and determine which package is right for your business. Book a consultation to get started." } },
          { "@type": "Question", "name": "What if I need more support than my package includes?", "acceptedAnswer": { "@type": "Answer", "text": "We're happy to help! Additional support can be arranged on an ad-hoc basis. Contact us to discuss your specific needs and we'll provide a quote for extra services." } },
          { "@type": "Question", "name": "Do you work with businesses outside Perth?", "acceptedAnswer": { "@type": "Answer", "text": "Yes! We work with businesses across Australia. Our services are location-independent, and we communicate with clients via email, WhatsApp, and Zoom calls." } },
          { "@type": "Question", "name": "What accounting software do you use?", "acceptedAnswer": { "@type": "Answer", "text": "We specialise in Xero, which is cloud-based and perfect for modern businesses. If you're using different software, we can discuss options to migrate or integrate with Xero." } },
          { "@type": "Question", "name": "How do you handle sensitive financial information?", "acceptedAnswer": { "@type": "Answer", "text": "Security is our top priority. We use encrypted connections, secure cloud storage, and follow strict confidentiality protocols. Your financial data is protected with industry-standard security measures." } },
          { "@type": "Question", "name": "What's the difference between a bookkeeper and an accountant?", "acceptedAnswer": { "@type": "Answer", "text": "A bookkeeper records and organises your day-to-day financial transactions — invoices, expenses, payroll, bank reconciliations, and BAS. An accountant typically handles tax returns, financial strategy, and year-end reporting. We work alongside your accountant to make their job (and yours) much easier. Many of our clients find they save money on accountant fees because their books are always clean and up-to-date." } },
          { "@type": "Question", "name": "Do I need to be in Perth to use your services?", "acceptedAnswer": { "@type": "Answer", "text": "No — we work with clients across Australia. All our services are delivered remotely via Xero and cloud-based tools. We communicate via email, WhatsApp, and Zoom. That said, we're proudly Perth-based and love working with local businesses." } },
          { "@type": "Question", "name": "I'm behind on my bookkeeping. Can you still help?", "acceptedAnswer": { "@type": "Answer", "text": "Absolutely. Catchup bookkeeping is one of our specialties. Whether you're a few months behind or a few years, we'll get your books in order without judgment. We offer a fixed-price catchup service starting from $210 so you know exactly what you're paying." } },
          { "@type": "Question", "name": "Do I need Xero already?", "acceptedAnswer": { "@type": "Answer", "text": "No. We can set up Xero for you from scratch, migrate you from another system (like MYOB or spreadsheets), or work within your existing Xero file. Xero setup and training starts from $80/hour." } },
          { "@type": "Question", "name": "What industries do you work with?", "acceptedAnswer": { "@type": "Answer", "text": "We work with small businesses across a wide range of industries including trades & construction, retail, hospitality, health & wellness, professional services, media & events, and more. If you run a business in Australia, we can help." } },
          { "@type": "Question", "name": "How do I send you my documents?", "acceptedAnswer": { "@type": "Answer", "text": "It's easy. Most clients use Xero's built-in receipt capture (via the Xero app on your phone), email, or a shared Dropbox/Google Drive folder. We'll set up the most convenient system for you during onboarding." } },
          { "@type": "Question", "name": "Are there lock-in contracts?", "acceptedAnswer": { "@type": "Answer", "text": "No lock-in contracts. We work on a month-to-month basis. We're confident you'll love the service, but we never want you to feel trapped. You can cancel with 30 days' notice at any time." } },
          { "@type": "Question", "name": "How quickly can you get started?", "acceptedAnswer": { "@type": "Answer", "text": "Most clients are fully onboarded within one week of signing up. We'll book your free consultation, send a proposal within 24 hours, and get your Xero file set up and bank feeds connected as quickly as possible." } }
        ]
      }) }} />
    </div>
  );
}
